import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculateAllFeatureWTP } from '../../lib/calculations';
import { formatPrice } from '../../lib/formatters';
import { InfoButton } from '../common/Tooltip';
import { GRID_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS } from '../../styles/chart-theme';
import { useIsMobile } from '../../hooks/useIsMobile';

export function WTPByFeature() {
  const isMobile = useIsMobile();
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);
  const assumptions = useSimulatorStore(s => s.assumptions);

  const data = useMemo(() => calculateAllFeatureWTP(selectedPrice, assumptions), [selectedPrice, assumptions]);
  const maxVal = Math.max(...data.map(d => d.impliedDollarValue));

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Willingness to Pay by Feature</h3>
        <InfoButton helpKey="wtpByFeature" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">Implied dollar value each feature adds at {formatPrice(selectedPrice)}. How much more would customers pay for a product with this feature?</p>

      <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 60, left: 150, bottom: 5 }}>
          <CartesianGrid {...GRID_PROPS} horizontal={false} vertical />
          <XAxis type="number" tick={{ fill: 'var(--color-axis-tick)', fontSize: 12 }} tickFormatter={v => formatPrice(v)} />
          <YAxis type="category" dataKey="name" {...Y_AXIS_PROPS} width={140} tick={{ fill: 'var(--color-text-primary)', fontSize: 12 }} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(value: number) => [formatPrice(Math.round(value)), 'Implied Value']} />
          <Bar dataKey="impliedDollarValue" radius={[0, 4, 4, 0]} maxBarSize={28} label={{ position: 'right', fill: 'var(--color-text-secondary)', fontSize: 11, formatter: (v: number) => formatPrice(Math.round(v)) }}>
            {data.map((entry, i) => (
              <Cell key={i} fill={CHART_COLORS.teal} fillOpacity={0.4 + 0.6 * (entry.impliedDollarValue / maxVal)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
